import Button from 'components/Form/Button';
import { Icon, Icons } from 'components/Icon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BankImg from '../../../../../../../assets/assets/bank.png';
import CircleProgressBar from 'components/CircleProgressBar';
import { Link } from 'react-router-dom';
import ConfirmModal from 'components/Modal/ConfirmModal';
import {
  useDeleteBank,
  useGetBankList
} from 'common/queries-and-mutations/bank';
import { Bank, Banks } from 'service/dto/bank.dto';
import { Allocation } from 'service/dto/allocation.dto';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import { Loader } from 'components/Loader';
import withCreatePortal from 'components/HOC/withCreatePortal';
import AddBankModal from './AddBankModal';

const EnhancedConfirmModal = withCreatePortal(ConfirmModal);
const EnhancedAddBankForm = withCreatePortal(AddBankModal);

export default function BankAssets() {
  const [showAskToConfirm, setShowAskToConfirm] = useState(false);
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [isExpand, setIsExpand] = useState(false);
  const [assetName, setAssetName] = useState('');
  const [bankId, setBankId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: assets, isLoading, refetch } = useGetBankList();
  const { mutate: deleteBank, isSuccess, isError } = useDeleteBank();

  const totalBeneficiary = useMemo(() => {
    const totalBeneficiary = (assets || [])?.reduce(
      (sum, trustee) => sum + trustee.totalBeneficiary,
      0
    );
    return (totalBeneficiary || 0) > 10
      ? totalBeneficiary
      : `0${totalBeneficiary}`;
  }, [assets]);

  const getBeneficiaryNames = useCallback((asset: Bank): string => {
    return asset.allocations
      .map(
        (allocation: Allocation) =>
          `${allocation?.beneficiary?.beneficiaryFirstName} ${
            allocation?.beneficiary?.beneficiaryMiddleName || ''
          } ${allocation?.beneficiary?.beneficiaryLastName}`
      )
      .join(', ');
  }, []);

  const getTrustNames = useCallback((asset: Banks | undefined): string => {
    const trustees = (asset ?? []).filter(ass => ass.trusteeId);
    if (trustees.length) {
      return trustees
        .map(
          ({ trustee }) =>
            `${
              trustee
                ? `${trustee.trusteeFirstName ?? ''} ${
                    trustee.trusteeMiddleName ?? ''
                  } ${trustee.trusteeLastName ?? ''}`
                : ''
            } `
        )
        .join(', ');
    }
    return 'N/A';
  }, []);

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      setShowAskToConfirm(false);
      refetch();
      toast(<Alert message="Asset Successfully Removed" type="success" />);
    }
  }, [isSuccess, isError, refetch]);

  return (
    <div>
      {!isLoading && assets && (
        <>
          <div className="mt-2 flex flex-col">
            <div className="w-full rounded-md bg-white shadow-sm">
              <div className="border-b border-[#E5E5E5]">
                <div
                  tabIndex={-1}
                  role="button"
                  className="flex items-center justify-between p-3 px-5"
                  onClick={() => setIsExpand(prevState => !prevState)}
                >
                  <span className="text-base font-normal text-[#34475E]">
                    Bank Account{' '}
                    <span className="ml-8 font-semibold">{assets?.length}</span>
                  </span>
                  <div className={isExpand ? 'rotate-180' : ''}>
                    <Icon name={Icons.ArrowDownCircle} />
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between px-4 pb-1">
                <div className="mr-3 space-y-2">
                  <img
                    src={BankImg}
                    className="h-[6.5rem] w-[6.5rem] object-contain"
                  />
                </div>
                <div className="my-2 mr-3 space-y-2">
                  <div className="font-circular text-sm font-medium text-slate-600">
                    Total Account
                  </div>
                  <div className="font-circular text-xl font-medium text-slate-600">
                    {assets && assets?.length < 10
                      ? `0${assets?.length}`
                      : assets?.length}
                  </div>
                </div>
                <div className="my-2 mr-3 space-y-2">
                  <div className="font-circular text-sm font-medium text-slate-600">
                    Added Beneficiary
                  </div>
                  <div className="font-circular text-xl font-medium text-slate-600">
                    {totalBeneficiary}
                  </div>
                </div>
                <div className="my-2 mr-3 space-y-2">
                  <div className="font-circular text-sm font-medium text-slate-600">
                    Assigned Trustee
                  </div>
                  <div className="font-circular text-xl font-medium text-slate-600">
                    {getTrustNames(assets) ?? 'N/A'}
                  </div>
                </div>
                <div className="mr-3 space-y-2">
                  <div className="flex flex-wrap items-center">
                    <Button
                      variant="outline"
                      size="custom"
                      className="w-32 place-self-end rounded border-[#FF7A00] bg-[#FF660008] px-3 py-2 text-center text-xs font-medium text-[#FF7A00]"
                      label="Add bank"
                      iconPosition="left"
                      icon={<Icon name={Icons.Add} width={10} height={10} />}
                      onClick={() => {
                        setShowAddAsset(true);
                      }}
                    ></Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {isExpand && (
            <div>
              {assets.map((asset, idx) => (
                <div className="mt-2 flex flex-col" key={asset.id}>
                  <div className="w-full rounded-md bg-white shadow-sm">
                    <div className="bg-[#FF660005]">
                      <div className="ml-5 flex items-center justify-between p-2 px-5">
                        <div className="flex flex-col">
                          <span className="text-base font-normal text-[#FF6606]">
                            Account {idx + 1}
                          </span>
                          <div className="mt-1 flex items-center gap-1 text-sm font-bold text-[#585858]">
                            {asset.bankName}{' '}
                            <Icon name={Icons.Bank} fill="#0000008F" />
                          </div>
                        </div>
                        <div className="mr-9 flex items-center gap-2">
                          <CircleProgressBar
                            progress={asset.percentageAlloted}
                            width={30}
                            height={50}
                          />
                          <div className="flex items-center gap-1 text-base text-[#868686]">
                            <span>{asset.percentageAlloted}%</span>
                            <span>Assigned</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="m-6 flex flex-wrap items-center justify-between px-4 pb-1">
                      <div className="my-2 mr-3 space-y-2">
                        <div className="font-circular text-[13px] font-normal text-[#7A7C87]">
                          Account name
                        </div>
                        <div className="font-circular text-base font-medium text-[#585858]">
                          {asset.accountName}
                        </div>
                      </div>
                      <div className="my-2 mr-3 space-y-2">
                        <div className="font-circular text-[13px] font-normal text-[#7A7C87]">
                          Account number
                        </div>
                        <div className="font-circular text-base font-medium text-[#585858]">
                          {asset.accountNumber}
                        </div>
                      </div>
                      <div className="my-2 mr-3 space-y-2">
                        <div className="font-circular text-[13px] font-normal text-[#7A7C87]">
                          Beneficiaries
                        </div>
                        <div className="w-[20rem] truncate font-circular text-base font-medium text-[#585858]">
                          {asset.allocations.length > 0
                            ? getBeneficiaryNames(asset)
                            : 'N/A'}
                        </div>
                      </div>
                      <div className="mr-3 space-y-2">
                        {isSubmitting ? (
                          <div className="flex flex-col items-center justify-center">
                            <Loader
                              color={'#FF6606'}
                              height={30}
                              width={30}
                              type="Wait"
                            />
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center gap-3">
                            <Button
                              variant="outline"
                              size="custom"
                              className="place-self-end rounded border-[#FF7A00] px-3 py-2 text-center text-xs font-medium text-[#FF7A00]"
                              label="Remove account"
                              iconPosition="right"
                              iconLabelSpacing={15}
                              icon={
                                <Icon name={Icons.VectorRight} fill="#FF6600" />
                              }
                              onClick={() => {
                                setShowAskToConfirm(true);
                                setBankId(asset.id);
                                setAssetName(asset.bankName);
                              }}
                            ></Button>
                            <Link to={`bank/details/${asset.id}`}>
                              <Button
                                variant="outline"
                                size="custom"
                                className="place-self-end rounded border-[#FF7A00] bg-[#FF7A00] px-3 py-2 text-center text-xs font-medium text-white"
                                label="View Beneficiaries"
                                iconPosition="right"
                                iconLabelSpacing={10}
                                icon={<Icon name={Icons.VectorRight} />}
                              ></Button>
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showAskToConfirm && (
        <EnhancedConfirmModal
          content={`Confirm the removal of ${assetName}?`}
          onConfirm={() => {
            deleteBank(bankId!);
          }}
          onCancel={() => {
            setShowAskToConfirm(false);
          }}
          actionText="Yes"
          cancelText="No"
          title="Action Required"
          isSubmitting={isSubmitting}
        />
      )}
      {showAddAsset && (
        <EnhancedAddBankForm
          onClose={() => setShowAddAsset(false)}
          onSuccess={() => {
            setShowAddAsset(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}
